import { createSignal, onCleanup, onMount } from 'solid-js';

const HELLO = '// hi there';

export function Welcome() {
  const [open, setOpen] = createSignal('');
  const [inner, setInner] = createSignal('');
  const [close_, setClose] = createSignal('');
  const [cls, setCls] = createSignal('');

  let cancelled = false;
  onCleanup(() => { cancelled = true; });

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  async function typeInto(set: (s: string) => void, text: string, baseMs = 55) {
    for (let i = 1; i <= text.length; i++) {
      if (cancelled) return;
      set(text.slice(0, i));
      await sleep(baseMs + Math.random() * 25);
    }
  }
  async function eraseFrom(get: () => string, set: (s: string) => void, ms = 22) {
    let s = get();
    while (s.length > 0) {
      if (cancelled) return;
      s = s.slice(0, -1);
      set(s);
      await sleep(ms);
    }
  }

  onMount(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setInner(HELLO);
      return;
    }
    void (async () => {
      const STYLED = [
        { open: '**', close: '**', cls: 'bold' },
        { open: '*', close: '*', cls: 'italic' },
        { open: '~~', close: '~~', cls: 'strike' },
      ];
      await sleep(220);
      for (const p of STYLED) {
        setCls(p.cls);
        await typeInto(setOpen, p.open, 70);
        await typeInto(setInner, HELLO, 55);
        await typeInto(setClose, p.close, 70);
        if (cancelled) return;
        await sleep(900);
        await eraseFrom(close_, setClose, 26);
        await eraseFrom(inner, setInner, 18);
        await eraseFrom(open, setOpen, 26);
        await sleep(140);
      }
      setCls('');
      await typeInto(setInner, HELLO, 55);
    })();
  });

  return (
    <div class="welcome">
      <article class="welcome-inner">
        <header class="welcome-hero">
          <p class="welcome-eyebrow mono mute">
            <span class="we-marker">{open()}</span><span class={`we-inner ${cls()}`}>{inner()}</span><span class="we-marker">{close_()}</span>
          </p>
          <h1>This is <span class="brand-glyph">md</span>.</h1>
          <p class="welcome-lede">
            A fast, opinionated markdown editor. Write in your browser, share a link
            to edit live with anyone, never set up an account. Your docs auto-save to
            your machine; collaboration is peer-to-peer.
          </p>
          <div class="welcome-cta-row">
            <a class="welcome-cta" href="/">
              <span>start writing</span>
              <span class="welcome-cta-arrow">→</span>
            </a>
            <span class="welcome-cta-quiet mono mute">no signup. nothing to install.</span>
          </div>
        </header>

        <section class="welcome-section">
          <p class="welcome-label mono mute">// what's in the box</p>
          <ul class="welcome-feature-grid">
            <li><span class="num mono mute">01</span><div><strong>Side-by-side editor + preview</strong><span>Source-mapped scroll sync, not the percentage-based fake.</span></div></li>
            <li><span class="num mono mute">02</span><div><strong>Live collaboration</strong><span>Share a link → edit together in real time, Yjs over WebRTC.</span></div></li>
            <li><span class="num mono mute">03</span><div><strong>Inline edit in the preview</strong><span>Double-click any block. Edits a single block in place.</span></div></li>
            <li><span class="num mono mute">04</span><div><strong>CSS templates</strong><span>YAML frontmatter picks the look: blog, RFC, resume, recipe.</span></div></li>
            <li><span class="num mono mute">05</span><div><strong>Math + code + tables</strong><span>KaTeX, Shiki, GFM. <code>⌘⌥T</code> reflows tables.</span></div></li>
            <li><span class="num mono mute">06</span><div><strong>Local-first</strong><span>Files live in your browser's IndexedDB. Export to disk anytime.</span></div></li>
          </ul>
        </section>

        <section class="welcome-author">
          <div class="author-photo-wrap">
            <img class="author-photo" src="/images/drew.jpg" alt="Andrew Khoury" />
            <span class="author-wave" aria-hidden="true">👋</span>
          </div>
          <div class="author-text">
            <p class="welcome-label mono mute">// hi there</p>
            <h2>I'm Andrew Khoury.</h2>
            <p class="author-lede">
              I build small, careful things that change how teams work — tools,
              experiments, and the occasional ten-page report nobody asked for.
            </p>
          </div>
        </section>

        <section class="welcome-section">
          <p class="welcome-label mono mute">// more awesome projects</p>
          <ul class="welcome-elsewhere">
            <li>
              <a href="https://portfolio-drew.web.app/">
                <div class="elsewhere-text">
                  <span class="elsewhere-name">Portfolio</span>
                  <span class="elsewhere-tag mute">10+ projects, 40+ experiments, and a running collaboration with AI</span>
                </div>
                <span class="elsewhere-arrow">→</span>
              </a>
            </li>
          </ul>
        </section>

        <p class="welcome-foot mono mute">
          built with solid + codemirror + yjs.
          <a href="/">enter the editor →</a>
        </p>
      </article>
    </div>
  );
}
